// Authentication logic for PLTX-SYNC Login
const JWT_SECRET = import.meta.env.VITE_JWT_SECRET || 'your-secret-key-change-in-production';

export interface User {
  username: string;
  clientName: string;
  clientUrl: string;
  loginTime: number;
  [key: string]: any; // JWT compatibility
}

export interface Client {
  name: string;
  username: string;
  password: string;
  url: string;
}

// Allowlist for safe return targets (includes previews)
const allowedReturnOrigins = [
  'http://localhost:5173',
  'https://exact-plytix-sync.vercel.app',
  'https://exact-plytix-sync-staging.vercel.app',
];

const isAllowedReturn = (urlStr: string | null) => {
  if (!urlStr) return false;
  try {
    const u = new URL(urlStr);
    if (allowedReturnOrigins.includes(u.origin)) return true;
    if (u.hostname.startsWith('exact-plytix-sync-staging-') && u.hostname.endsWith('.vercel.app')) return true;
    return false;
  } catch {
    return false;
  }
};

export const getReturnUrl = (defaultUrl: string) => {
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('return');
  if (isAllowedReturn(returnUrl)) return returnUrl!;
  return defaultUrl; // fallback to configured client
};

// Dynamic client detection via env
export const getClientsFromEnv = (): Client[] => {
  const clients: Client[] = [];
  const env = import.meta.env;
  const processedKeys = new Set<string>();

  Object.keys(env).forEach((key) => {
    if (!key.startsWith('VITE_CLIENT_') || !key.includes('_USERNAME')) return;

    const newFormatMatch = key.match(/^VITE_CLIENT_([A-Z0-9]+)_USERNAME_([A-Z0-9]+)$/);
    if (newFormatMatch) {
      const [, system, client] = newFormatMatch;
      const uniqueKey = `${system}_${client}`;
      if (processedKeys.has(uniqueKey)) return;
      processedKeys.add(uniqueKey);

      const username = env[`VITE_CLIENT_${system}_USERNAME_${client}`];
      const password = env[`VITE_CLIENT_${system}_PASSWORD_${client}`];
      const url = env[`VITE_CLIENT_${system}_URL_${client}`];

      if (username && password && url) {
        clients.push({
          name: `${system.toLowerCase()}_${client.toLowerCase()}`,
          username,
          password,
          url,
        });
      }
    } else {
      const oldFormatMatch = key.match(/^VITE_CLIENT_([A-Z0-9]+)_USERNAME$/);
      if (oldFormatMatch) {
        const [, clientName] = oldFormatMatch;
        if (processedKeys.has(clientName)) return;
        processedKeys.add(clientName);

        const username = env[`VITE_CLIENT_${clientName}_USERNAME`];
        const password = env[`VITE_CLIENT_${clientName}_PASSWORD`];
        const url = env[`VITE_CLIENT_${clientName}_URL`];

        if (username && password && url) {
          clients.push({
            name: clientName.toLowerCase(),
            username,
            password,
            url,
          });
        }
      }
    }
  });

  return clients;
};

export const authenticateUser = (username: string, password: string): Client | null => {
  const clients = getClientsFromEnv();
  return clients.find((c) => c.username === username && c.password === password) || null;
};

export const createAuthToken = async (client: Client): Promise<string> => {
  const user: User = {
    username: client.username,
    clientName: client.name,
    clientUrl: client.url,
    loginTime: Date.now(),
  };

  const header = { alg: 'HS256', typ: 'JWT' };
  const payload = {
    ...user,
    exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
    iat: Math.floor(Date.now() / 1000),
  };

  const encodedHeader = btoa(JSON.stringify(header)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  const encodedPayload = btoa(JSON.stringify(payload)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  const signature = await createHmacSignature(`${encodedHeader}.${encodedPayload}`, JWT_SECRET);

  return `${encodedHeader}.${encodedPayload}.${signature}`;
};

// Simple HMAC-SHA256 for browser
const createHmacSignature = async (data: string, secret: string): Promise<string> => {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const messageData = encoder.encode(data);

  const key = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', key, messageData);
  const signatureArray = new Uint8Array(signature);
  return btoa(String.fromCharCode(...signatureArray)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export const redirectToApp = (targetUrl: string, token: string) => {
  // Respect full return URL or fallback host; always attach token
  const url = targetUrl.startsWith('http') ? new URL(targetUrl) : new URL(`https://${targetUrl}`);
  url.searchParams.set('token', token);
  window.location.href = url.toString();
};

export const setAuthCookie = (token: string) => {
  document.cookie = `pltx-auth=${token}; path=/; max-age=86400; secure; samesite=none`;
  document.cookie = `pltx-auth=${token}; path=/; domain=.vercel.app; max-age=86400; secure; samesite=none`;
};

export const clearAuthCookie = () => {
  document.cookie = 'pltx-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'pltx-auth=; path=/; domain=.vercel.app; expires=Thu, 01 Jan 1970 00:00:00 GMT';
};

export const checkExistingAuth = async (): Promise<boolean> => {
  const cookies = document.cookie.split(';');
  const authCookie = cookies.find((cookie) => cookie.trim().startsWith('pltx-auth='));

  if (authCookie) {
    const token = authCookie.split('=')[1];
    try {
      return !!token && token.length > 0;
    } catch {
      clearAuthCookie();
      return false;
    }
  }

  return false;
};
